import * as Sentry from "@sentry/react";
import type { AnyRouter } from "@tanstack/react-router";
import type { RootOptions } from "react-dom/client";
import { getAnalyticsConsent } from "@repo/shared/analyticsConsent";
import { observeReplayConsent } from "./sentryConsent";
import { isServerUnreachableError } from "./environments/primary/bootstrapRequest";
import { isStaleChunkMessage } from "./lib/staleChunkReload";

import {
  sanitizeSentryBreadcrumb,
  sanitizeSentryEvent,
  sanitizeSentryReplayEvent,
  sanitizeSentrySpan,
} from "./sentry.privacy";

// A browser DSN is public configuration, not an API/auth token.
const HOSTED_DSN =
  "https://4335f57142778a47506ae3a4465fba50@o4512084802404352.ingest.de.sentry.io/4512084825473104";

interface SentryConfig {
  hostname: string;
  production: boolean;
  dsn?: string;
  enabled?: string;
  environment: string;
  release: string;
}

const REPLAY_SESSION_SAMPLE_RATE = 0.1;

let stopReplayConsent: (() => void) | undefined;

/** One issue for every stale-tab chunk failure, whatever the file or engine. */
export const STALE_CHUNK_FINGERPRINT = ["stale-chunk-load"];

/**
 * The error-event gate before anything leaves the browser:
 * - An unreachable server (timeout, failed connection, 502/503/504) is the
 *   server restarting or down, not a client bug; the error screen retries it
 *   (AMOREG-EVE-3, AMOREG-EVE-E).
 * - A stale-tab chunk failure names a different hashed file after every
 *   deploy, so Sentry opened a new issue each time (AMOREG-EVE-5); one
 *   fingerprint keeps them together, as a warning since the tab reloads.
 * Everything kept is scrubbed by sanitizeSentryEvent.
 */
export function prepareSentryErrorEvent<T extends Sentry.Event>(
  event: T,
  hint?: Sentry.EventHint,
): T | null {
  if (isServerUnreachableError(hint?.originalException)) return null;
  const sanitized = sanitizeSentryEvent(event);
  const staleChunk = sanitized.exception?.values?.some(
    (exception) => exception.value !== undefined && isStaleChunkMessage(exception.value),
  );
  if (staleChunk) {
    sanitized.fingerprint = STALE_CHUNK_FINGERPRINT;
    sanitized.level = "warning";
  }
  return sanitized;
}

export function initializeSentry(config: SentryConfig): boolean {
  const hosted = ["platform.amoreg.com", "dev.amoreg.com"].includes(config.hostname);
  const dsn = config.dsn?.trim() || (hosted ? HOSTED_DSN : undefined);
  if (!dsn || config.enabled === "false" || (!config.production && config.enabled !== "true")) {
    return false;
  }

  stopReplayConsent?.();
  stopReplayConsent = undefined;
  Sentry.init({
    dsn,
    environment: config.environment,
    release: config.release,
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: { request: false, response: false },
      httpBodies: [],
      urlQueryParams: false,
      genAI: { inputs: false, outputs: false },
      databaseQueryData: false,
      stackFrameVariables: false,
      frameContextLines: 0,
    },
    integrations: (defaults) => [
      // Release-health sessions count visits independently of error reports.
      ...defaults.filter((integration) => integration.name !== "BrowserSession"),
      Sentry.breadcrumbsIntegration({ console: false, dom: false }),
    ],
    tracesSampler: () => getAnalyticsConsent() === "accepted" ? 0.1 : 0,
    // No propagation to auth or document-provider requests.
    tracePropagationTargets: [],
    replaysSessionSampleRate: REPLAY_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate: 1,
    enableLogs: false,
    beforeSend: prepareSentryErrorEvent,
    beforeSendTransaction: (event) => getAnalyticsConsent() === "accepted" ? sanitizeSentryEvent(event) : null,
    beforeBreadcrumb: sanitizeSentryBreadcrumb,
    beforeSendSpan: sanitizeSentrySpan,
  });
  Sentry.addEventProcessor((event) => {
    if (event.type === "replay_event" && getAnalyticsConsent() !== "accepted") return null;
    return sanitizeSentryReplayEvent(event);
  });
  stopReplayConsent = observeReplayConsent(() => {
    const replay = Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        maskAttributes: ["title", "alt", "aria-label", "placeholder"],
        block: ['[href*="?"]', '[href*="#"]', "iframe"],
        networkDetailAllowUrls: [],
        networkCaptureBodies: false,
        // Keep the DOM recording and settings; omit custom console/network
        // breadcrumbs and performance metadata, which can carry raw URLs.
        beforeAddRecordingEvent: (event) => (event.data.tag === "options" ? event : null),
    });
    Sentry.addIntegration(replay);
    return {
      stop: () => replay.stop({ flush: false }),
      resume: () => {
        // Manual start bypasses SDK sampling, so sample a new session here.
        if (Math.random() < REPLAY_SESSION_SAMPLE_RATE) replay.start();
        else replay.startBuffering();
      },
    };
  });
  return true;
}

/** Attach once the real router exists, before its first render/load. */
export function instrumentSentryRouter(router: AnyRouter): void {
  if (!Sentry.isEnabled()) return;
  // The SDK deduplicates integrations by name, including their subscriptions.
  Sentry.addIntegration(Sentry.tanstackRouterBrowserTracingIntegration(router));
}

// main.tsx imports this before the rest of the app's module graph.
export const sentryEnabled =
  typeof window !== "undefined" &&
  initializeSentry({
    hostname: window.location.hostname,
    production: import.meta.env.PROD,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    enabled: import.meta.env.VITE_SENTRY_ENABLED,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE || `amoreg-web@${import.meta.env.APP_VERSION}`,
  });

// React 19 reports boundary-caught errors through the root too, including
// TanStack Router and RenderErrorBoundary. Keep the default handlers when off.
export function reportSentryReactError(
  error: unknown,
  errorInfo: { componentStack?: string | undefined },
): void {
  // React DOM permits explicit undefined; Sentry expects null or omission
  // under exactOptionalPropertyTypes.
  Sentry.reactErrorHandler()(error, { componentStack: errorInfo.componentStack ?? null });
}

export const sentryReactRootOptions: RootOptions = sentryEnabled
  ? {
      onCaughtError: reportSentryReactError,
      onUncaughtError: reportSentryReactError,
      onRecoverableError: reportSentryReactError,
    }
  : {};

/**
 * Whether the reports queued so far actually left the browser. `flush`
 * resolves true only once the transport drained before the timeout — with
 * Sentry off, offline, or blocked it resolves false, and the error screen
 * must not claim anyone was told.
 */
export function confirmSentryDelivery(timeoutMs = 4000): Promise<boolean> {
  if (!sentryEnabled) return Promise.resolve(false);
  return Sentry.flush(timeoutMs).catch(() => false);
}
